import React, { useEffect, useRef } from "react";
import { container } from "@/utils/mobx";
import { useInputEvent } from "@/components/toolkit/InputProvider";
import { SWIPE_GUARD_CLASS } from "@/utils/swipe";
import { MIN_FONT_SIZE, MAX_FONT_SIZE } from "@/components/toolkit/MonacoEditor/store";
import { MdPreviewStore } from "./store";
import "./s.md_preview.css";

export const MdPreview = container((props: {
    store: MdPreviewStore;
    html: string;
    fontSize: number;
    onFontSizeChange: (size: number) => void;
}) => {
    const store = props.store;
    const previewRef = useRef<HTMLDivElement | null>(null);
    const pinchStartSizeRef = useRef(store.fontSize);

    useInputEvent('ctrl_wheel', (e) => {
        const delta = e.deltaY < 0 ? 1 : -1;
        store.onFontSizeChange(Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, store.fontSize + delta)));
        return true;
    }, [store], { target: previewRef });

    useInputEvent('pinch', (e) => {
        if (e.phase === 'start') {
            pinchStartSizeRef.current = store.fontSize;
        } else if (e.phase === 'change') {
            const next = Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, Math.round(pinchStartSizeRef.current * e.scale)));
            if (next !== store.fontSize) store.onFontSizeChange(next);
        }
    }, [store], { target: previewRef });

    useEffect(() => {
        const el = previewRef.current;
        if (!el) return;
        // Non-passive so we can preventDefault during a two-finger pinch, preventing
        // the browser from native-scrolling the element while we handle the gesture.
        const preventDefault = (e: TouchEvent) => { if (e.touches.length >= 2) e.preventDefault(); };
        el.addEventListener('touchmove', preventDefault, { passive: false });
        return () => el.removeEventListener('touchmove', preventDefault);
    }, []);

    return (
        <div
            ref={previewRef}
            className={`md_preview ${SWIPE_GUARD_CLASS}`}
            style={{ fontSize: store.fontSize }}
            dangerouslySetInnerHTML={{ __html: store.html }}
        />
    );
}, MdPreviewStore);
